import { Lucide } from '../../../index';
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import useEmblaCarousel from 'embla-carousel-react';
import { Clock, MapPin } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppointmentProjectSelector } from './appointment-project-selector';
import { AppointmentModalHeader } from './shared';
import { EAppointmentType, TAppointmentProjectMinimal } from './types';
import { TimeOption } from './utils';
import { useAppointmentModalActions, useAppointmentModalState } from './store';

interface AppointmentStep1Props {
  projects: TAppointmentProjectMinimal[];
  onClose?: () => void;
  t: (key: string) => string;
  warningBanner?: React.ReactNode;
  hideDuration?: boolean;
  logoSrc?: string;
  isLanding?: boolean;
}

export const AppointmentStep1: React.FC<AppointmentStep1Props> = ({
  projects,
  onClose,
  t,
  warningBanner,
  hideDuration = false,
  logoSrc,
  isLanding = false,
}) => {
  const theme = useTheme();

  const {
    getTimeSlotsForDate,
    setProjectId,
    setDateTimeSlot,
    setStep,
    setAppointmentType,
    setDuration,
    setNotes,
    setSelectedDate,
    setSelectedTime,
    setTimeOptions,
  } = useAppointmentModalActions();
  const {
    projectId,
    appointmentType,
    duration,
    notes,
    dateOptions,
    selectedDate,
    selectedTime,
    timeOptions,
  } = useAppointmentModalState();

  // Embla Carousel hooks
  const [dateEmblaRef, dateEmblaApi] = useEmblaCarousel({
    align: 'center',
    containScroll: 'trimSnaps',
    dragFree: true,
    direction: theme.direction,
  });
  const [timeEmblaRef] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
    direction: theme.direction,
  });
  const [showProjectError, setShowProjectError] = useState<boolean>(false);
  const [hasScrolledToInitialDate, setHasScrolledToInitialDate] = useState<boolean>(false);

  // Auto-scroll to selected date initially
  useEffect(() => {
    if (dateEmblaApi && selectedDate && dateOptions.length > 0 && !hasScrolledToInitialDate) {
      const selectedIndex = dateOptions.findIndex(
        (option) => option.date.getTime() === selectedDate.getTime(),
      );
      if (selectedIndex !== -1) {
        // Add timeout to ensure carousel is fully initialized
        const timeoutId = setTimeout(() => {
          dateEmblaApi.scrollTo(selectedIndex);
          setHasScrolledToInitialDate(true);
        }, 100);

        return () => clearTimeout(timeoutId);
      }
    }
  }, [dateEmblaApi, selectedDate, dateOptions, hasScrolledToInitialDate]);

  // Translated duration options (values in minutes)
  const durationOptions = useMemo(
    () => [
      { value: 30, label: t('scheduling.appointment.duration30Min') },
      { value: 60, label: t('scheduling.appointment.duration1Hour') },
      { value: 90, label: t('scheduling.appointment.duration1_5Hours') },
      { value: 120, label: t('scheduling.appointment.duration2Hours') },
    ],
    [t],
  );
  // Translated appointment type options
  const appointmentTypeOptions = useMemo(
    () => [
      {
        value: EAppointmentType.GOOGLE_MEET,
        label: t('scheduling.appointment.googleMeet'),
        icon: (
          <Box
            component="img"
            src="https://treedis-public.s3.eu-central-1.amazonaws.com/scheduling/email-templates/assets/google_meet_icon.webp"
            alt="Google Meet"
            sx={{ width: 22, height: 22 }}
          />
        ),
      },
      {
        value: EAppointmentType.PHYSICAL,
        label: t('scheduling.appointment.physicalMeeting'),
        icon: <MapPin size={18} />,
      },
    ],
    [t],
  );

  const handleProjectChange = (projectId?: number) => {
    if (projectId) {
      setProjectId(projectId);
      setShowProjectError(false);
    }
  };

  // Handle date selection
  const handleDateSelect = useCallback(
    (index: number) => {
      const newDate = dateOptions[index].date;
      setSelectedDate(newDate);

      const timeOptions = getTimeSlotsForDate(newDate);
      setTimeOptions(timeOptions);

      if (timeOptions.length > 0) {
        setSelectedTime(timeOptions[0]);
      } else {
        setSelectedTime(undefined);
      }
    },
    [dateOptions, getTimeSlotsForDate],
  );

  // Handle time selection
  const handleTimeSelect = useCallback((timeOption: TimeOption) => {
    setSelectedTime(timeOption);
  }, []);

  const handleContinue = useCallback(() => {
    if (!projectId) {
      setShowProjectError(true);
      return;
    }

    if (selectedDate && selectedTime) {
      setDateTimeSlot({
        start: selectedTime.start,
        end: selectedTime.end,
      });
      setStep(2);
    }
  }, [selectedDate, selectedTime]);

  return (
    <Stack spacing={3} width="100%">
      <AppointmentModalHeader onClose={onClose} t={t} logoSrc={logoSrc} isLanding={isLanding}
      />

      {warningBanner}

      {projects.length > 1 && (
        <AppointmentProjectSelector
          selectedProjectId={projectId}
          onProjectChange={handleProjectChange}
          projects={projects}
          error={showProjectError}
          helperText={showProjectError ? t('scheduling.appointment.projectRequired') : ''}
          t={t}
        />
      )}

      <Stack spacing={1} width="100%">
        <Typography variant="body2" fontWeight={500} color="text.secondary">
          {t('scheduling.appointment.selectDate')}
        </Typography>
        <Box
          ref={dateEmblaRef}
          sx={{
            overflow: 'hidden',
            width: '100%',
          }}
        >
          <Stack direction="row" gap={1.25} py={0.25} px={0.5} dir="ltr">
            {dateOptions.length === 0 ? (
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                width="100%"
                py={0.25}
                sx={{
                  bgcolor: 'grey.50',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'grey.300',
                  height: 82,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('scheduling.appointment.noAvailableDates')}
                </Typography>
              </Stack>
            ) : (
              dateOptions.map((dateOption, index) => {
                const isSelected =
                  selectedDate && selectedDate.getTime() === dateOption.date.getTime();
                return (
                  <Button
                    key={`${dateOption.day}-${dateOption.dateStr}-${dateOption.date.getTime()}`}
                    onClick={() => handleDateSelect(index)}
                    variant="outlined"
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 82,
                      height: 82,
                      p: 1.75,
                      borderRadius: '12px',
                      border: isSelected ? 2 : 1,
                      borderColor: isSelected ? 'primary.main' : 'grey.300',
                      flexShrink: 0,
                      minWidth: 'unset',
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        borderColor: isSelected ? 'primary.main' : 'grey.400',
                      },
                    }}
                  >
                    <Typography color="text.secondary" variant="body1">
                      {dateOption.day}
                    </Typography>
                    <Typography color="text.primary" variant="body1" fontWeight={600}>
                      {dateOption.dateStr}
                    </Typography>
                  </Button>
                );
              })
            )}
          </Stack>
        </Box>
      </Stack>

      <Stack spacing={1} width="100%">
        <Typography variant="body2" fontWeight={500} color="text.secondary">
          {t('scheduling.appointment.selectTime')}
        </Typography>
        {timeOptions.length === 0 ? (
          <Stack
            direction="row"
            justifyContent="center"
            alignItems="center"
            width="100%"
            py={0.25}
            sx={{
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: 1,
              borderColor: 'grey.300',
              height: 42,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {t('scheduling.appointment.noAvailableTimeSlots')}
            </Typography>
          </Stack>
        ) : (
          <Box
            ref={timeEmblaRef}
            sx={{
              overflow: 'hidden',
              width: '100%',
            }}
          >
            <Stack direction="row" gap={1.25} py={0.25} px={0.5} dir="ltr">
              {timeOptions.map((timeOption, index) => {
                const isSelected = selectedTime && selectedTime.start === timeOption.start;
                return (
                  <Button
                    key={`${timeOption.time}-${index}`}
                    onClick={() => handleTimeSelect(timeOption)}
                    variant="outlined"
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: 'none',
                      px: 1.75,
                      py: 1.25,
                      borderRadius: '12px',
                      border: isSelected ? 2 : 1,
                      borderColor: isSelected ? 'primary.main' : 'grey.300',
                      flexShrink: 0,
                      minWidth: 'unset',
                      textTransform: 'none',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        borderColor: isSelected ? 'primary.main' : 'grey.400',
                      },
                    }}
                  >
                    <Typography color="text.primary" variant="body1" fontWeight={600}>
                      {timeOption.time}
                    </Typography>
                  </Button>
                );
              })}
            </Stack>
          </Box>
        )}
      </Stack>

      <Stack direction="row" spacing={2.5} width="100%">
        {!hideDuration && (
          <Stack spacing={0.75} width="100%">
            <Typography variant="body2" fontWeight={500} color="text.secondary">
              {t('scheduling.appointment.duration')}
            </Typography>
            <Select
              size="small"
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value))}
              startAdornment={
                <InputAdornment position="start">
                  <Lucide icon={Clock} />
                </InputAdornment>
              }
              MenuProps={{
                sx: {
                  zIndex: 10000, // Ensure dropdown appears above mobile drawer
                },
              }}
            >
              {durationOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Stack>
        )}

        <Stack spacing={0.75} width="100%">
          <Typography variant="body2" fontWeight={500} color="text.secondary">
            {t('scheduling.appointment.appointmentType')}
          </Typography>
          <Select
            size="small"
            value={appointmentType}
            onChange={(e) => setAppointmentType(e.target.value as EAppointmentType)}
            MenuProps={{
              sx: {
                zIndex: 10000, // Ensure dropdown appears above mobile drawer
              },
            }}
          >
            {appointmentTypeOptions.map((option, index) => (
              <MenuItem key={index} value={option.value}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {option.icon}
                  {option.label}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </Stack>
      </Stack>

      <Stack spacing={0.75} width="100%">
        <Typography variant="body2" fontWeight={500} color="text.secondary">
          {t('scheduling.appointment.notes')}
        </Typography>
        <TextField
          multiline
          rows={5}
          placeholder={t('scheduling.appointment.notesPlaceholder')}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          sx={{ borderRadius: 2 }}
        />
      </Stack>

      <Button
        variant="contained"
        color="primary"
        fullWidth
        onClick={handleContinue}
        disabled={!selectedDate || !selectedTime}
      >
        {t('scheduling.appointment.continue')}
      </Button>
    </Stack>
  );
};
